// frontend/src/components/surveys/SurveyConfiguration.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Settings, BarChart3, ChevronLeft, ChevronRight,
    Lock, Save, Monitor, Car, Library
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput, ThemedBadge } from '../themed';
import { useScrollableTabs } from '../../hooks/useScrollableTabs';
import { isSystemAdmin } from '../../utils/roleUtils';
import surveyService from '../../services/surveyService';
import SurveyAnalytics from './SurveyAnalytics';
import QuestionLibraryManager from './QuestionLibraryManager';
import ServiceSurveyManager from './ServiceSurveyManager';
import SystemSurveyManager from './SystemSurveyManager';

/**
 * Survey Configuration Component for Admins
 * Following NotificationSettings pattern for preference control and multi-language support
 */
const SurveyConfiguration = ({ entityId }) => {
    const { t } = useTranslation();
    const { text, border, cn } = useTheme();
    const userRole = localStorage.getItem('role');

    // State management
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('preferences');
    const [requestedLibrarySurveyType, setRequestedLibrarySurveyType] = useState(null);

    const [permissions, setPermissions] = useState({
        canManageService: false,
        canManageSystem: false,
        canViewAnalytics: false
    });
    
    // Survey preferences state
    const [preferences, setPreferences] = useState({
        service_survey_enabled: false,
        system_survey_enabled: false,
        survey_display_delay_seconds: 5
    });
    const tabScrollRefreshKey = [
        userRole,
        permissions.canManageService,
        permissions.canManageSystem,
        permissions.canViewAnalytics,
        preferences.service_survey_enabled,
        preferences.system_survey_enabled
    ].join('|');
    const {
        tabsRef,
        canScrollLeft,
        canScrollRight,
        checkTabsScroll,
        scrollTabs
    } = useScrollableTabs({ refreshKey: tabScrollRefreshKey });

    // Load initial data
    useEffect(() => {
        loadSurveyData();
    }, [entityId]);

    // Handle active tab visibility when preferences change
    useEffect(() => {
        const visibleTabs = [
            {
                id: 'preferences',
                visible: permissions.canManageService || permissions.canManageSystem
            },
            {
                id: 'library',
                visible: permissions.canManageService || permissions.canManageSystem
            },
            {
                id: 'service',
                visible: permissions.canManageService && preferences.service_survey_enabled
            },
            {
                id: 'system',
                visible: permissions.canManageSystem && isSystemAdmin(userRole)
            },
            {
                id: 'analytics',
                visible: (permissions.canManageService && preferences.service_survey_enabled) ||
                         (permissions.canManageSystem && preferences.system_survey_enabled)
            }
        ].filter(tab => tab.visible);

        // If current active tab is no longer visible, switch to first visible tab
        if (!visibleTabs.find(tab => tab.id === activeTab) && visibleTabs.length > 0) {
            setActiveTab(visibleTabs[0].id);
        }
    }, [preferences, permissions, activeTab, userRole]);

    const handleTabClick = (tabId) => {
        // Clear one-shot library requests on ordinary tab navigation so direct
        // Question Library entry keeps its existing permission-based default.
        setRequestedLibrarySurveyType(null);
        setActiveTab(tabId);
    };

    const handleOpenQuestionLibrary = (surveyType) => {
        setRequestedLibrarySurveyType(surveyType);
        setActiveTab('library');
    };

    /**
     * Load all survey data including permissions, preferences, and configurations
     */
    const loadSurveyData = async () => {
        try {
            setLoading(true);

            // Load permissions, preferences, and configurations in parallel
            const [perms, prefs] = await Promise.all([
                surveyService.checkSurveyPermissions(entityId),
                surveyService.getSurveyPreferences(entityId),
                surveyService.getSurveyConfiguration(entityId, 'service'),
                surveyService.getSurveyConfiguration(entityId, 'system')
            ]);

            setPermissions(perms);
            setPreferences(prefs);

            // Set initial active tab - always start with 'preferences' if user has any permissions
            // The tab visibility will be filtered based on permissions and entity settings
        } catch (error) {
            console.error('Error loading survey data:', error);
            toast.error(t('errorLoadingSurveyData'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Save survey preferences
     */
    const handleSavePreferences = async () => {
        try {
            setSaving(true);
            await surveyService.updateSurveyPreferences(entityId, preferences);
            toast.success(t('surveyPreferencesSaved'));
        } catch (error) {
            console.error('Error saving preferences:', error);
            toast.error(t('errorSavingPreferences'));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Update survey configuration
     */
    const handleSaveConfiguration = async (config) => {
        try {
            setSaving(true);
            const surveyType = config.survey_type || (activeTab === 'service' ? 'service' : 'system');
            await surveyService.updateSurveyConfiguration(entityId, surveyType, config);

            toast.success(t('surveyConfigurationSaved'));
        } catch (error) {
            console.error('Error saving configuration:', error);
            toast.error(t('errorSavingConfiguration'));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Define available tabs based on permissions and entity preferences
     *
     * Visibility Rules:
     * - Preferences: Show if user has required role for at least one globally active survey type
     * - Question Library: Show if user has required role for at least one survey type (sys_admin or required_role)
     * - Service Survey: Globally active + user has role + entity enabled
     * - System Survey: Only for sys_admin (view global system survey)
     * - Analytics: At least one survey type is (globally active + user has role + entity enabled)
     */
    const tabs = [
        {
            id: 'preferences',
            label: t('preferences'),
            icon: Settings,
            description: t('manageSurveyPreferences'),
            // Show if user has management permission for at least one survey type
            visible: permissions.canManageService || permissions.canManageSystem
        },
        {
            id: 'library',
            label: t('questionLibrary'),
            icon: Library,
            description: t('manageGlobalQuestions'),
            // Show if user can manage questions (has required_role or above)
            visible: permissions.canManageService || permissions.canManageSystem
        },
        {
            id: 'service',
            label: t('serviceSurvey'),
            icon: Car,
            description: t('configureServiceQualitySurvey'),
            // Show if: globally active + user has role + entity enabled
            visible: permissions.canManageService && preferences.service_survey_enabled
        },
        {
            id: 'system',
            label: t('systemSurvey'),
            icon: Monitor,
            description: t('viewGlobalSystemSurvey'),
            // Show for system admins to view global system survey
            visible: permissions.canManageSystem && isSystemAdmin(userRole)
        },
        {
            id: 'analytics',
            label: t('analytics'),
            icon: BarChart3,
            description: t('viewSurveyAnalytics'),
            // Show if at least one survey type meets all three conditions
            visible: (permissions.canManageService && preferences.service_survey_enabled) ||
                     (permissions.canManageSystem && preferences.system_survey_enabled)
        }
    ].filter(tab => tab.visible);

    // Loading state
    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loading')}...</span>
            </div>
        );
    }

    // No permissions state
    if (tabs.length === 0) {
        return (
            <ThemedCard className="p-8">
                <div className={cn("text-center", text.secondary)}>
                    <Lock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h2 className={cn("text-xl font-semibold mb-2", text.primary)}>
                        {t('noSurveyPermissions')}
                    </h2>
                    <p>{t('contactAdministratorForAccess')}</p>
                </div>
            </ThemedCard>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className={cn("text-2xl font-bold", text.primary)}>
                        {t('surveySettings')}
                    </h1>
                    <p className={cn("text-sm mt-1", text.secondary)}>
                        {t('manageSurveyConfigurationAndAnalytics')}
                    </p>
                </div>

                {/* Status badges - Only show for surveys user can manage */}
                <div className="flex items-center gap-3">
                    {permissions.canManageService && (
                        <ThemedBadge variant={preferences.service_survey_enabled ? "success" : "secondary"}>
                            <Car className="w-3 h-3 mr-1" />
                            {preferences.service_survey_enabled ? t('serviceEnabled') : t('serviceDisabled')}
                        </ThemedBadge>
                    )}
                    {permissions.canManageSystem && (
                        <ThemedBadge variant={preferences.system_survey_enabled ? "success" : "secondary"}>
                            <Monitor className="w-3 h-3 mr-1" />
                            {preferences.system_survey_enabled ? t('systemEnabled') : t('systemDisabled')}
                        </ThemedBadge>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className={cn("border-b relative", border.default)}>
                {/* Left scroll indicator */}
                {canScrollLeft && (
                    <button
                        onClick={() => scrollTabs('left')}
                        className={cn(
                            "absolute left-0 top-0 bottom-0 z-10 px-1 flex items-center",
                            "bg-gradient-to-r from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
                        )}
                        aria-label="Scroll tabs left"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                )}

                {/* Right scroll indicator */}
                {canScrollRight && (
                    <button
                        onClick={() => scrollTabs('right')}
                        className={cn(
                            "absolute right-0 top-0 bottom-0 z-10 px-1 flex items-center",
                            "bg-gradient-to-l from-white via-white to-transparent dark:from-gray-900 dark:via-gray-900"
                        )}
                        aria-label="Scroll tabs right"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                )}

                <div
                    ref={tabsRef}
                    onScroll={checkTabsScroll}
                    className="flex space-x-6 overflow-x-auto scrollbar-thin"
                >
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab.id)}
                                className={cn(
                                    "flex items-center gap-2 pb-3 px-1 border-b-2 transition-colors whitespace-nowrap flex-shrink-0",
                                    activeTab === tab.id
                                        ? "border-blue-500 text-blue-500"
                                        : cn("border-transparent", text.secondary, "hover:text-gray-700 dark:hover:text-gray-300")
                                )}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="font-medium">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">
                {/* Preferences Tab */}
                {activeTab === 'preferences' && (
                    <ThemedCard className="p-6">
                        <div className="space-y-6">
                            {/* Service Survey Toggle - Only show if globally active AND user has required role */}
                            {permissions.canManageService && (
                                <div className="flex items-center justify-between p-4 rounded-lg border">
                                    <div className="flex-1">
                                        <h3 className={cn("font-medium text-lg", text.primary)}>
                                            {t('serviceQualitySurvey')}
                                        </h3>
                                        <p className={cn("text-sm mt-1", text.secondary)}>
                                            {t('collectFeedbackAboutDriverAndVehicle')}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={preferences.service_survey_enabled}
                                                onChange={(e) => setPreferences({
                                                    ...preferences,
                                                    service_survey_enabled: e.target.checked
                                                })}
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* System Survey Toggle - Only show if globally active AND user has required role */}
                            {permissions.canManageSystem && (
                                <div className="flex items-center justify-between p-4 rounded-lg border">
                                    <div className="flex-1">
                                        <h3 className={cn("font-medium text-lg", text.primary)}>
                                            {t('systemFeedbackSurvey')}
                                        </h3>
                                        <p className={cn("text-sm mt-1", text.secondary)}>
                                            {t('gatherUserExperienceFeedback')}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={preferences.system_survey_enabled}
                                                onChange={(e) => setPreferences({
                                                    ...preferences,
                                                    system_survey_enabled: e.target.checked
                                                })}
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Display Delay Setting */}
                            <div className="p-4 rounded-lg border">
                                <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                    {t('surveyDisplayDelay')}
                                </label>
                                <div className="flex items-center gap-3">
                                    <ThemedInput
                                        type="number"
                                        value={preferences.survey_display_delay_seconds}
                                        onChange={(e) => setPreferences({
                                            ...preferences,
                                            survey_display_delay_seconds: parseInt(e.target.value) || 0
                                        })}
                                        min="0"
                                        max="60"
                                        className="w-24"
                                    />
                                    <span className={cn("text-sm", text.secondary)}>
                                        {t('seconds')}
                                    </span>
                                </div>
                                <p className={cn("text-xs mt-2", text.secondary)}>
                                    {t('delayAfterTripCompletionBeforeShowingSurvey')}
                                </p>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end pt-4">
                                <ThemedButton
                                    onClick={handleSavePreferences}
                                    disabled={saving || (!permissions.canManageService && !permissions.canManageSystem)}
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? t('saving') : t('savePreferences')}
                                </ThemedButton>
                            </div>
                        </div>
                    </ThemedCard>
                )}

                {/* Question Library Tab */}
                {activeTab === 'library' && (
                    <QuestionLibraryManager
                        entityId={entityId}
                        requestedSurveyType={requestedLibrarySurveyType}
                    />
                )}

                {/* Service Survey Tab */}
                {activeTab === 'service' && (
                    <ServiceSurveyManager
                        entityId={entityId}
                        onSave={handleSaveConfiguration}
                        saving={saving}
                    />
                )}

                {/* System Survey Tab */}
                {activeTab === 'system' && (
                    <SystemSurveyManager
                        entityId={entityId}
                        onManageQuestions={handleOpenQuestionLibrary}
                    />
                )}

                {/* Analytics Tab */}
                {activeTab === 'analytics' && (
                    <SurveyAnalytics
                        entityId={entityId}
                        canViewService={permissions.canManageService}
                        canViewSystem={permissions.canManageSystem}
                    />
                )}
            </div>
        </div>
    );
};

export default SurveyConfiguration;
